/**
 * `diffPlans`: what a re-run changes (plan §12.1 step 8: "each run reproduces the last one
 * plus whatever changed"). Records are compared by natural key.
 * @module
 */
import type { ImportPlan } from './types.js';

/** The record lists of a plan that are compared. */
export const DIFFED_SECTIONS = [
  'categories',
  'families',
  'products',
  'identifiers',
  'recipes',
  'stateRules',
  'channelMappings',
  'uomConversions',
  'outcomes',
  'reviewItems',
] as const;
/** One of {@link DIFFED_SECTIONS}. */
export type DiffedSection = (typeof DIFFED_SECTIONS)[number];

/** Changes in one section, as sorted key lists. */
export interface SectionDiff {
  added: string[];
  changed: string[];
  removed: string[];
}

/** Result of {@link diffPlans}. */
export interface PlanDiff {
  sections: Record<DiffedSection, SectionDiff>;
  /** True when nothing differs. */
  unchanged: boolean;
  /** Counts per section, e.g. `products: +3 ~1 -0`. Sections without changes are left out. */
  summary: string[];
}

function keyOf(section: DiffedSection, record: object): string {
  return section === 'outcomes'
    ? (record as { sourceRef: string }).sourceRef
    : (record as { key: string }).key;
}

/** Stable JSON: object keys sorted, so key order never counts as a change. */
function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : 1));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stable(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

/** What changed from `previous` to `next`, by natural key (outcomes by source ref). */
export function diffPlans(previous: ImportPlan, next: ImportPlan): PlanDiff {
  const sections = {} as Record<DiffedSection, SectionDiff>;
  const summary: string[] = [];
  for (const section of DIFFED_SECTIONS) {
    const before = new Map<string, string>(
      (previous[section] as object[]).map((r) => [keyOf(section, r), stable(r)]),
    );
    const after = new Map<string, string>(
      (next[section] as object[]).map((r) => [keyOf(section, r), stable(r)]),
    );
    const diff: SectionDiff = { added: [], changed: [], removed: [] };
    for (const [key, value] of after) {
      const old = before.get(key);
      if (old === undefined) diff.added.push(key);
      else if (old !== value) diff.changed.push(key);
    }
    for (const key of before.keys()) if (!after.has(key)) diff.removed.push(key);
    diff.added.sort();
    diff.changed.sort();
    diff.removed.sort();
    sections[section] = diff;
    if (diff.added.length + diff.changed.length + diff.removed.length > 0) {
      summary.push(
        `${section}: +${diff.added.length} ~${diff.changed.length} -${diff.removed.length}`,
      );
    }
  }
  return { sections, unchanged: summary.length === 0, summary };
}
